import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useLanguageContext } from "../../../Context/LanguageContext";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import "dayjs/locale/de"; // for German
import "dayjs/locale/en"; // for English

export default function BulkExportInspectionReport({
  handleBulkExportInspectionReport,
}) {
  const { language, localization } = useLanguageContext();
  const [open, setOpen] = useState(false);
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [error, setError] = useState("");
  const [combinePdf, setCombinePdf] = useState(false);

  const isValidDate = (date) => {
    return dayjs.isDayjs(date) && date.isValid();
  };

  useEffect(() => {
    if (!fromDate || !toDate) {
      setError(localization.selectBothDates);
      return;
    }

    if (!isValidDate(fromDate) || !isValidDate(toDate)) {
      setError(localization.invalidDate);
      return;
    }

    if (toDate.isBefore(fromDate)) {
      setError(localization.toDateMustBeAfterFromDate);
      return;
    }

    const diffInDays = toDate.diff(fromDate, "days");

    if (diffInDays > 60) {
      setError(localization.dateRangeExceedsTwoMonth);
      return;
    }

    if (toDate.isAfter(dayjs())) {
      setError(localization.futureDateNotAllowed);
      return;
    }

    setError("");
  }, [fromDate, toDate]);

  const handleOpen = () => {
    setFromDate(dayjs());
    setToDate(dayjs());
    setError("");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCombinePdf(false);
  };

  const handleDownload = () => {
    handleBulkExportInspectionReport(
      fromDate.toDate(),
      toDate.toDate(),
      combinePdf
    );
    handleClose();
  };

  return (
    <>
      <Button
        variant="contained"
        className="gridTableButton gridTableButtonStyle"
        style={{ marginLeft: 8 }}
        onClick={handleOpen}
      >
        {localization.bulkExportInspectionReport}
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{localization.selectDateRange}</DialogTitle>
        <DialogContent>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={language === "de" ? "de" : "en"}
          >
            <DatePicker
              label={localization.From}
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
              sx={{ width: "100%", mb: 2, mt: 2 }}
              maxDate={dayjs()}
            />
            <DatePicker
              label={localization.to}
              value={toDate}
              onChange={(newValue) => setToDate(newValue)}
              sx={{ width: "100%", mb: 2 }}
              maxDate={dayjs()}
            />
          </LocalizationProvider>
          <FormControlLabel
            control={
              <Checkbox
                checked={combinePdf}
                onChange={(e) => setCombinePdf(e.target.checked)}
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: 28, // Makes the checkbox bigger
                  },
                }}
              />
            }
            label={localization.combineReports}
            sx={{
              mt: 1,
              "& .MuiFormControlLabel-label": {
                fontWeight: "normal !important", // Makes the label text non-bold
              },
            }}
          />
          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{localization.cancel}</Button>
          <Button
            onClick={handleDownload}
            disabled={
              !!error ||
              !fromDate ||
              !toDate ||
              !isValidDate(fromDate) ||
              !isValidDate(toDate)
            }
          >
            {localization.download}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
